import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartPie, 
  faChartBar, 
  faUsers, 
  faListAlt, 
  faTicketAlt 
} from '@fortawesome/free-solid-svg-icons';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { 
  FaChartPie as FaChartPieIcon,
  FaCheckCircle, 
  FaLayerGroup, 
  FaHourglassHalf, 
  FaUserPlus, 
  FaCalendarAlt, 
  FaMedal, 
  FaGraduationCap, 
  FaProjectDiagram,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaUsers as FaUsersIcon,
  FaUserGraduate,
  FaChartLine,
  FaExclamationTriangle
} from 'react-icons/fa';

// Components
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import ProjectActivityChart from '../components/ProjectActivityChart';
import MemberGrowthLineChart from '../components/MemberGrowthLineChart';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);


// Helper: generate monthly growth data from members
function generateMemberGrowthData(members) {
  if (!Array.isArray(members)) return [];
  // Group by month
  const counts = {};
  members.forEach(m => {
    if (!m.RegDate) return;
    const d = new Date(m.RegDate);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  // Sort months and accumulate
  const months = Object.keys(counts).sort();
  let cumulative = 0;
  return months.map(month => {
    cumulative += counts[month];
    return { month, count: cumulative };
  });
}

export default function ClubStatistics() {
  // State declarations
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  // Calculate yearly growth data
  const yearlyGrowthData = useMemo(() => {
    const years = {};
    
    members.forEach(member => {
      if (!member.RegDate) return;
      
      const year = new Date(member.RegDate).getFullYear();
      if (!years[year]) {
        years[year] = 0;
      }
      years[year]++;
    });
    
    // Sort years in ascending order
    const sortedYears = {};
    Object.keys(years).sort().forEach(key => {
      sortedYears[key] = years[key];
    });
    
    return sortedYears;
  }, [members]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'RegDate', direction: 'desc' });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const API_BASE = 'https://dev.sliitleo.org/Backend';

useEffect(() => {
  setLoading(true);
  fetch(`${API_BASE}/member_api.php?action=list_basic`, {
    headers: { Accept: 'application/json' }
  })
    .then(async (res) => {
      // Handle HTTP errors BEFORE trying to parse JSON
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`member_api ${res.status} ${res.statusText} ${text}`);
      }
      return res.json();
    })
    .then((payload) => {
      // Support either a raw array OR {success, data}
      const arr = Array.isArray(payload) ? payload
               : (payload && Array.isArray(payload.data)) ? payload.data
               : [];
      setMembers(arr);
    })
    .catch((err) => {
      console.error('member_api failed:', err);
      toast.error('Failed to load member data. Please try again later.');
    })
    .finally(() => setLoading(false));
}, []);


  // Debounce search input
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 300);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  // Filter and sort members
  const filteredMembers = useMemo(() => {
    let result = [...members];
    
    // Debug: Log first member to check structure
  
    // Apply search filter
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      console.log('Searching for:', query);
      
      result = result.filter(member => {
        // Get SLIIT ID (checking common field names)
        const sliitId = (
          member.SLIITID || 
          member.sliitId || 
          member.studentId || 
          member.StudentID || 
          ''
        ).toString().toLowerCase().trim();
        
        // Get full name (checking common field names)
        const fullName = (
          member.Name || 
          member.name || 
          `${member.FirstName || ''} ${member.LastName || ''}`.trim()
        ).toLowerCase();
        
        // Get all member fields for search
        const memberFields = {
          name: fullName,
          email: (member.Email || member.email || '').toLowerCase(),
          phone: (member.Phone || member.phone || '').toLowerCase(),
          department: (member.Department || member.department || '').toLowerCase(),
          id: (member.id || member.ID || '').toString().toLowerCase(),
          rollNo: (member.RollNo || member.rollNo || '').toLowerCase(),
          batch: (member.Batch || member.batch || '').toLowerCase(),
          section: (member.Section || member.section || '').toLowerCase(),
          sliitId: sliitId
        };
        
        // Combine all fields into a single search string
        const searchString = Object.values(memberFields).join(' ');
        
        // Debug: Log search details for first few matches
        if (result.indexOf(member) < 3) {
          console.log('Searching member:', { 
            name: memberFields.name, 
            sliitId: memberFields.sliitId,
            searchString 
          });
        }
        
        // Check if query matches SLIIT ID exactly (without spaces)
        if (sliitId && sliitId.replace(/\s+/g, '') === query.replace(/\s+/g, '')) {
          console.log('Found by SLIIT ID match:', sliitId);
          return true;
        }
        
        // Check if query matches full name exactly (case insensitive)
        if (fullName && fullName === query.toLowerCase()) {
          console.log('Found by full name match:', fullName);
          return true;
        }

        // Check if all search terms are found in any field
        const searchTerms = query.split(/\s+/);
        const allTermsMatch = searchTerms.every(term => 
          Object.values(memberFields).some(field => 
            field.includes(term)
          )
        );
        
        if (allTermsMatch) {
          console.log('Found by partial match for terms:', searchTerms);
        }
        
        return allTermsMatch;
      });
    }
    
    // Filter by academic year (July to June)
    if (selectedYear) {
      const [startYear] = selectedYear.split('-').map(Number);
      const startDate = new Date(startYear, 6, 1); // July 1 of start year
      const endDate = new Date(startYear + 1, 6, 1); // July 1 of next year
      
      result = result.filter(member => {
        // If member has an academicYear field that matches, include them
        if (member.academicYear && member.academicYear === selectedYear) {
          return true;
        }
        
        // If no registration date, exclude
        if (!member.RegDate) return false;
        
        // Parse the registration date
        let regDate;
        try {
          regDate = new Date(member.RegDate);
          // If date is invalid, exclude
          if (isNaN(regDate.getTime())) return false;
        } catch (e) {
          return false;
        }
        
        // Check if registration date falls within the academic year
        return regDate >= startDate && regDate < endDate;
      });
      
      // console.log(`Filtered for ${selectedYear} (${startDate.toDateString()} - ${new Date(endDate - 1).toDateString()}):`, result.length, 'members');
    }
    
    // Apply sorting
    // replace your sort block with:
if (sortConfig.key) {
  result.sort((a, b) => {
    const av = (a?.[sortConfig.key] ?? '').toString().toLowerCase();
    const bv = (b?.[sortConfig.key] ?? '').toString().toLowerCase();

    if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
    if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
}

    
    return result;
  }, [members, searchQuery, selectedYear, sortConfig]);
  
  // Pagination logic
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const currentItems = filteredMembers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // Handle sort request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };
  
  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1 text-indigo-400" /> 
      : <FaSortDown className="ml-1 text-indigo-400" />;
  };
  
  // Export data function (to be implemented)
  const handleExport = () => {
    // This will be implemented when we add the export functionality
    // console.log('Exporting data...');
  };

  // Helper function to check if a date is within the current academic year (July to June)
  const isInCurrentAcademicYear = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11 (Jan-Dec)
    
    // Academic year starts in July (month 6)
    const academicYearStart = new Date(currentYear, 6, 1); // July 1 of current year
    const academicYearEnd = new Date(currentYear + 1, 5, 30); // June 30 of next year
    
    // If current month is before July, adjust the academic year
    if (currentMonth < 6) {
      academicYearStart.setFullYear(currentYear - 1);
      academicYearEnd.setFullYear(currentYear);
    }
    
    return d >= academicYearStart && d <= academicYearEnd;
  };

  // Compute statistics using current date
  const now = new Date();
  const oneWeekAgo = new Date(now);
  oneWeekAgo.setDate(now.getDate() - 7);
  
  const total = members.length;
  
  // New members this week
  const newcomersWeek = members.filter(m => {
    if (!m.RegDate) return false;
    const regDate = new Date(m.RegDate);
    return regDate >= oneWeekAgo && regDate <= now;
  }).length;
  
  // New members this month (current calendar month)
  const newcomersMonth = members.filter(m => {
    if (!m.RegDate) return false;
    const regDate = new Date(m.RegDate);
    return regDate.getMonth() === now.getMonth() && 
           regDate.getFullYear() === now.getFullYear();
  }).length;
  
  // Use the current date for academic year calculations
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11 (Jan=0)
  
  // Determine current academic year (July to June)
  const academicYearStart = new Date(
    currentMonth >= 6 ? currentYear : currentYear - 1, // If month is July or later, use current year, else previous year
    6, // July (0-indexed)
    1 // 1st day of month
  );
    
  const academicYearEnd = new Date(
    academicYearStart.getFullYear() + 1, // Next year
    5, // June (0-indexed)
    30 // Last day of June
  );
  
  // Calculate new members in current academic year
  const newcomersAcademicYear = members.filter(m => {
    if (!m.RegDate) return false;
    const regDate = new Date(m.RegDate);
    return regDate >= academicYearStart && regDate <= academicYearEnd;
  }).length;
  
  // Format academic year string (e.g., "2025-26")
  const academicYearStr = `${academicYearStart.getFullYear()}-${(academicYearStart.getFullYear() + 1).toString().slice(-2)}`;

  // Initialize counters for year levels and graduates
  let year1Count = 0, year2Count = 0, year3Count = 0, year4Count = 0, graduatesCount = 0;
  
  members.forEach(member => {
    // Declare regDate at the start of the loop
    let regDate = null;
    
    // Check if member is explicitly marked as graduate
    const isExplicitGraduate = (member.Year && member.Year.toString().toLowerCase().includes('grad')) || 
                             (member.Status && member.Status.toString().toLowerCase().includes('grad'));
    
    // If no registration date, check explicit graduate status only
    if (!member.RegDate || member.RegDate === '0000-00-00' || member.RegDate === '0000-00-00 00:00:00') {
      if (isExplicitGraduate) graduatesCount++;
      return;
    }
    
    try {
      regDate = new Date(member.RegDate);
      if (isNaN(regDate.getTime())) {
        if (isExplicitGraduate) graduatesCount++;
        return;
      }
      
      // Calculate years since registration
      let yearsDiff = currentYear - regDate.getFullYear();
      
      // Adjust for month (if current month is before registration month, subtract a year)
      if (currentMonth < regDate.getMonth()) {
        yearsDiff--;
      }
      
      // Determine year level based on years since registration
      if (yearsDiff >= 4) {
        graduatesCount++;
      } else if (yearsDiff === 3) {
        year4Count++;
      } else if (yearsDiff === 2) {
        year3Count++;
      } else if (yearsDiff === 1) {
        year2Count++;
      } else if (yearsDiff === 0) {
        year1Count++;
      } else if (isExplicitGraduate) {
        // Handle case where registration date is in the future but marked as graduate
        graduatesCount++;
      }
    } catch (e) {
      // If there's an error parsing the date, just check explicit graduate status
      if (isExplicitGraduate) graduatesCount++;
      return; // Skip to next iteration if there was an error
    }
    
    // If registration date is valid, proceed with classification
    try {
      // Calculate academic year based on registration date (July to June)
      const regYear = regDate.getFullYear();
      const regMonth = regDate.getMonth(); // 0-11 (Jan-Dec)
      
      // Academic year starts in July (month 6)
      const academicStartYear = regMonth < 6 ? regYear - 1 : regYear;
      
      // Current academic year (July 2025 - June 2026 is 2025 academic year)
      const currentAcademicYear = currentMonth < 6 ? currentYear - 1 : currentYear;
      
      // Years since registration (1 = first year, 2 = second year, etc.)
      const yearsSinceRegistration = currentAcademicYear - academicStartYear + 1;
      
      // Categorize member based on years since registration
      if (isExplicitGraduate || (yearsSinceRegistration > 4 && regDate < new Date('2025-07-01'))) {
        graduatesCount++;
      } else if (yearsSinceRegistration === 1) {
        year1Count++;
      } else if (yearsSinceRegistration === 2) {
        year2Count++;
      } else if (yearsSinceRegistration === 3) {
        year3Count++;
      } else if (yearsSinceRegistration === 4) {
        year4Count++;
      }
    } catch (e) {
      // console.error('Error processing member registration date:', e);
      // If there's an error, just check explicit graduate status
      if (isExplicitGraduate) graduatesCount++;
    }
  });

  // Registration year-based groups
  const regYears = members.map(m => m.RegDate ? new Date(m.RegDate).getFullYear() : null).filter(y => y);
  const registrationYear = now.getFullYear();
  const prevYears = [registrationYear, registrationYear-1, registrationYear-2, registrationYear-3];
  const yearCounts = prevYears.map(y => regYears.filter(ry => ry === y).length);
  const previousYearsCount = regYears.filter(ry => ry < registrationYear-3).length;

  return (
    <div className="flex bg-[#111827] text-white min-h-screen">
      {/* Sidebar: hidden on mobile, visible on md+ */}
      <Sidebar className="hidden md:block" />
      {/* Hamburger menu for mobile */}
      <Topbar user={user} />
      {/* Sidebar drawer for mobile (optional, simple inline for now) */}
      {/* Main content: responsive margin/padding */}
      <main className="ml-0 md:ml-64 mt-14 w-full px-2 sm:px-4 md:px-8 py-4 md:py-10">
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-5 mb-8 flex items-center gap-4 shadow-lg">
          <span className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-600 mb-3">
              <FontAwesomeIcon icon={faChartPie} className="text-white-600 text-xl" />
            </div>
            <span className="text-lg font-bold tracking-wide text-white drop-shadow">Club Dashboard <span className="text-gray-400 font-normal">/ Club Statistics</span></span>
          </span>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap gap-3 mt-4">
            <Link to="/club-statistics" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-orange-400 group">
              <span className="text-gray-400 text-lg"><FontAwesomeIcon icon={faChartPie} /></span>
              Club Statistics
            </Link>
              <Link to="/reporting-hub" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-blue-400 group">
                <span className="text-blue-400 text-lg"><FontAwesomeIcon icon={faChartBar} /></span>
                Reporting Hub
              </Link>
              <Link to="/member-database" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-red-400 group">
                <span className="text-red-400 text-lg"><FontAwesomeIcon icon={faUsers} /></span>
                Member Database
              </Link>
              <Link to="/category-selection" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-green-400 group">
                 <span className="text-green-400 text-lg"><FontAwesomeIcon icon={faListAlt} /></span>
                 Category Selections
                </Link>
                <Link to="/tickets-admin" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-pink-400 group">
                 <span className="text-pink-400 text-lg"><FontAwesomeIcon icon={faTicketAlt} /></span>
                 Tickets Admin
                </Link>
                </div>
          </div>
        
            {/* Club Projects and Club Membership Dashboard Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-6 md:mb-10">
              {/* Club Projects Card */}
              <div className="bg-[#23272f] rounded-2xl p-3 sm:p-4 md:p-6 shadow-xl border border-gray-800 flex-1">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-600 mb-3">
                  <FaProjectDiagram className="text-white text-xl" />
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-white text-lg font-bold">Club Projects</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px]">
                    <div className="text-3xl font-bold">0</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaChartPieIcon /></span> Total Projects</div>
                  </div>
                  <div className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px]">
                    <div className="text-3xl font-bold">0</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaCheckCircle className="text-green-400" /></span> Completed Projects</div>
                  </div>
                  <div className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px]">
                    <div className="text-3xl font-bold">0</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaLayerGroup className="text-blue-400" /></span> Total Phase Count</div>
                  </div>
                  <div className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] col-span-1 md:col-span-3">
                    <div className="text-3xl font-bold">0</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaHourglassHalf className="text-yellow-400" /></span> Pending Report Count</div>
                  </div>
                </div>
              </div>
              {/* Club Membership Card */}
              <div className="bg-[#23272f] rounded-2xl p-3 sm:p-4 md:p-6 shadow-xl border border-gray-800 flex-1">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-600 mb-3">
                  <FaUsersIcon className="text-white-600 text-xl" />
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-white text-lg font-bold">Club Membership</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <Link to="/member-database" className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] hover:bg-[#23272f] transition cursor-pointer">
                    <div className="text-3xl font-bold">{loading ? '...' : total}</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaUsersIcon /></span> Total Membership</div>
                  </Link>
                  <Link to="/member-database?newcomers=month" className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] hover:bg-[#23272f] transition cursor-pointer">
                    <div className="text-3xl font-bold">{loading ? '...' : newcomersMonth}</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaUserPlus className="text-green-400" /></span> New comers for the month</div>
                  </Link>
                  <Link to="/member-database?newcomers=year" className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] hover:bg-[#23272f] transition cursor-pointer">
                    <div className="text-3xl font-bold">{loading ? '...' : newcomersAcademicYear}</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaCalendarAlt className="text-blue-400" /></span> New comers this Leostic year (2025 July- 2026 June)</div>
                  </Link>
                  <Link to="/member-database?year=1" className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] hover:bg-[#23272f] transition cursor-pointer">
                    <div className="text-3xl font-bold">{loading ? '...' : year1Count}</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaMedal className="text-gray-300" /></span> Year 01</div>
                  </Link>
                  <Link to="/member-database?year=2" className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] hover:bg-[#23272f] transition cursor-pointer">
                    <div className="text-3xl font-bold">{loading ? '...' : year2Count}</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaMedal className="text-blue-400" /></span> Year 02</div>
                  </Link>
                  <Link to="/member-database?year=3" className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] hover:bg-[#23272f] transition cursor-pointer">
                    <div className="text-3xl font-bold">{loading ? '...' : year3Count}</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaMedal className="text-yellow-400" /></span> Year 03</div>
                  </Link>
                  <Link to="/member-database?year=4" className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] hover:bg-[#23272f] transition cursor-pointer">
                    <div className="text-3xl font-bold">{loading ? '...' : year4Count}</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaMedal className="text-red-400" /></span> Year 04</div>
                  </Link>
                  <Link to="/member-database?year=graduated" className="bg-[#181b20] rounded-xl p-4 flex flex-col items-start border border-gray-700 min-w-[120px] hover:bg-[#23272f] transition cursor-pointer">
                    <div className="text-3xl font-bold">{loading ? '...' : graduatesCount}</div>
                    <div className="flex items-center gap-2 text-gray-400 mt-1 text-sm"><span className="text-base"><FaGraduationCap className="text-green-300" /></span> Graduates</div>
                  </Link>
                </div>
                
              
               
              </div>
            </div>

            {/* Member Distribution Statistics */}
            <div className="bg-[#23272f] rounded-2xl p-3 sm:p-4 md:p-6 border border-gray-800 shadow-xl mb-8">
              <div className="flex   items-center mb-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-600 mb-3">
                  <FaUsersIcon className="text-white-600 text-xl" />
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <br/>
                  <span className="text-white text-lg font-bold">   Member Distribution</span>
                  {/* <h3 className="text-xl font-semibold text-gray-300">Member Distribution</h3> */}
                </div>
                
               
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  <p className="mt-2 text-gray-400">Loading member data...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* By Faculty */}
                  <div className="bg-[#1a1f2e] rounded-xl p-3 sm:p-4 md:p-6 border border-gray-800">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-600 mb-3">
                  <FaUsersIcon className="text-white-600 text-xl" />
                </div>
                    <h4 className="text-lg font-semibold text-gray-300 mb-4">Faculties</h4>
                    <div className="space-y-3">
                      {[
                        { name: 'Faculty of Business Management', color: 'blue-400' },
                        { name: 'Faculty of Computing', color: 'green-400' },
                        { name: 'Faculty of Human Sciences', color: 'yellow-400' }
                      ].map((faculty, index) => {
                        const count = members.filter(member => 
                          member.Faculty === faculty.name && 
                          member.RegDate && 
                          new Date(member.RegDate) >= new Date('2025-07-01') && 
                          new Date(member.RegDate) <= new Date('2026-06-30')
                        ).length;
                        
                        return (
                          <div key={index} className="flex justify-between items-center">
                            <span className="text-gray-400">{faculty.name}</span>
                            <span className={`text-xl font-bold text-${faculty.color}`}>
                              {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-[#1a1f2e] rounded-xl p-3 sm:p-4 md:p-6 border border-gray-800">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-600 mb-3">
                  <FaUsersIcon className="text-white-600 text-xl" />
                </div>
                    <h4 className="text-lg font-semibold text-gray-300 mb-4">Membership by Academic Year</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Current Academic Year (2025-2026) */}
                      <div className="bg-[#1e2533] p-4 rounded-lg    ">
                        <h5 className="text-blue-400 font-semibold mb-3">2025-2026 Academic Year</h5>
                        <div className="space-y-3">
                          {[
                            { name: '1st Year', year: 'YEAR 1', color: 'green-400', startDate: '2025-07-01', endDate: '2026-06-30' },
                            { name: '2nd Year', year: 'YEAR 2', color: 'yellow-400', startDate: '2025-07-01', endDate: '2026-06-30' },
                            { name: '3rd Year', year: 'YEAR 3', color: 'purple-400', startDate: '2025-07-01', endDate: '2026-06-30' },
                            { name: '4th Year', year: 'YEAR 4', color: 'red-400', startDate: '2025-07-01', endDate: '2026-06-30' }
                          ].map((item, index) => {
                            const currentYearMembers = members.filter(member => {
                              if (!member.RegDate) return false;
                              
                              const regDate = new Date(member.RegDate);
                              const isInCurrentAcademicYear = 
                                regDate >= new Date(item.startDate) && 
                                regDate <= new Date(item.endDate);
                              
                              if (!isInCurrentAcademicYear) return false;
                              
                              const memberYear = member.Year?.toString().trim();
                              return memberYear === item.year;
                            });

                            return (
                              <div key={index} className="flex justify-between items-center">
                                <span className="text-gray-300">{item.name}</span>
                                <span className={`text-sm font-semibold text-${item.color}`}>
                                  {currentYearMembers.length} members
                                </span>
                              </div>
                            );
                          })}
                          
                          {/* Total for current academic year */}
                          <div className="mt-4 pt-3 border-t border-gray-700">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-300 font-medium">Total</span>
                              <span className="text-blue-400 font-bold">
                                {members.filter(member => {
                                  if (!member.RegDate) return false;
                                  const regDate = new Date(member.RegDate);
                                  return regDate >= new Date('2025-07-01') && regDate <= new Date('2026-06-30');
                                }).length} members
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Previous Academic Years Summary */}
                      <div className="bg-[#1e2533] p-4 rounded-lg border-l border-gray-700">
                        <h5 className="text-blue-400 font-semibold mb-3">Previous Academic Years</h5>
                        <div className="space-y-3">
                          {[
                            { year: '2024-2025', startDate: '2024-07-01', endDate: '2025-06-30' },
                            { year: '2023-2024', startDate: '2023-07-01', endDate: '2024-06-30' },
                            { year: '2022-2023', startDate: '2022-07-01', endDate: '2023-06-30' },
                            { year: '2021-2022', startDate: '0000-00-00', endDate: '0000-00-01' }
                          ].map((item, index) => {
                            const yearMembers = members.filter(member => {
                              // For 2021-2022, include all members with invalid/empty registration dates
                              if (item.year === '2021-2022') {
                                return !member.RegDate || member.RegDate === '0000-00-00' || member.RegDate === '0000-00-00 00:00:00';
                              }
                              
                              // For other years, use date range filtering
                              if (!member.RegDate || member.RegDate === '0000-00-00' || member.RegDate === '0000-00-00 00:00:00') {
                                return false;
                              }
                              
                              try {
                                const regDate = new Date(member.RegDate);
                                if (isNaN(regDate.getTime())) return false;
                                return regDate >= new Date(item.startDate) && regDate <= new Date(item.endDate);
                              } catch (e) {
                                return false;
                              }
                            });

                            return (
                              <div key={index} className="flex justify-between items-center">
                                <span className="text-gray-300">{item.year}</span>
                                <span className="text-gray-400">
                                  {yearMembers.length} member{yearMembers.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                    
                </div>
              )}
            </div>
            <br/><br/>

            {/* Year-wise Membership Growth Chart */}
            

            {/* Member Growth Line Chart */}
            <div className="bg-[#23272f] rounded-2xl  shadow-xl flex flex-col items-center justify-center   pt-7">
          {/* <div className="text-gray-400 text-xl font-semibold mb-2">Member Growth Over Time</div> */}
          <div className="w-full min-h-[200px] h-60 md:h-80 bg-[#181b20] rounded-xl flex items-center justify-center">
            <MemberGrowthLineChart data={generateMemberGrowthData(members)} />
          </div>
        </div>
        <br/><br/>        <br/><br/>
        {/* Member Details Table */}
        <div className="bg-[#23272f] rounded-2xl p-3 sm:p-4 md:p-6 border border-gray-800 shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2 sm:gap-4">
            <h3 className="text-gray-200 text-xl font-semibold">Member Details</h3>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <select 
                className="bg-gray-700 text-gray-200 rounded-lg px-2 py-2 sm:px-3 text-sm border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setCurrentPage(1);
                }}
                value={selectedYear}
              >
                <option value="">All Academic Years</option>
                <option value="2022-23">2022-23 (Jul 2022 - Jun 2023)</option>
                <option value="2023-24">2023-24 (Jul 2023 - Jun 2024)</option>
                <option value="2024-25">2024-25 (Jul 2024 - Jun 2025)</option>
                <option value="2025-26">2025-26 (Jul 2025 - Jun 2026)</option>
              </select>
              <div className="relative w-full sm:w-72">
                <input
                  type="text"
                  placeholder="Search by SLIIT ID, full name, email, department..."
                  className="bg-gray-700 text-gray-200 rounded-lg px-2 py-2 pr-8 sm:px-3 sm:py-2 text-sm border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 focus:outline-none"
                    aria-label="Clear search"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {!searchQuery && (
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="bg-gray-700 text-gray-200 rounded-lg px-2 py-2 sm:px-3 text-sm border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-24"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="5">5 per page</option>
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
                <button 
                  onClick={() => {
                    // Filter members for 2025-2026 academic year
                    const academicYearMembers = members.filter(member => {
                      if (!member.RegDate) return false;
                      const regDate = new Date(member.RegDate);
                      return regDate >= new Date('2025-07-01') && regDate <= new Date('2026-06-30');
                    });

                    if (academicYearMembers.length === 0) {
                      toast.warning('No members found for the 2025-2026 academic year', {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                      });
                      return;
                    }

                    // Convert to CSV
                    const headers = ['Leo ID', 'SLIIT ID', 'Roll No', 'Full Name', 'Email', 'Faculty', 'Year', 'Registration Date'];
                    const csvContent = [
                      headers.join(','),
                      ...academicYearMembers.map(member => [
                        `"${member.SLIITLeoID || ''}"`,
                        `"${member.SLIITID || ''}"`,
                        `"${member.RollNo || ''}"`,
                        `"${member.FullName || ''}"`,
                        `"${member.Email || ''}"`,
                        `"${member.Faculty || ''}"`,
                        `"${member.Year || ''}"`,
                        `"${member.RegDate || ''}"`,

                      ].join(','))
                    ].join('\n');

                    // Create and trigger download
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.setAttribute('href', url);
                    link.setAttribute('download', `members_2025-2026_${new Date().toISOString().split('T')[0]}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-2 py-2 sm:px-3 text-sm flex items-center gap-1.5 transition-colors"
                  title="Export 2025-2026 Members"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="hidden sm:inline">Export 2025-26</span>
                </button>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700 text-xs sm:text-sm">
              <thead className="bg-gray-800">
                <tr>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700"
                    onClick={() => requestSort('SLIITLeoID')}
                  >
                    <div className="flex items-center">
                      Leo ID
                      {getSortIcon('SLIITLeoID')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700"
                    onClick={() => requestSort('FullName')}
                  >
                    <div className="flex items-center">
                      Full Name
                      {getSortIcon('FullName')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 hidden md:table-cell"
                    onClick={() => requestSort('SLIITID')}
                  >
                    <div className="flex items-center">
                      SLIIT ID
                      {getSortIcon('SLIITID')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 hidden lg:table-cell"
                    onClick={() => requestSort('Faculty')}
                  >
                    <div className="flex items-center">
                      Faculty
                      {getSortIcon('Faculty')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 hidden sm:table-cell"
                    onClick={() => requestSort('Year')}
                  >
                    <div className="flex items-center">
                      Year
                      {getSortIcon('Year')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 hidden lg:table-cell"
                    onClick={() => requestSort('Semester')}
                  >
                    <div className="flex items-center">
                      Semester
                      {getSortIcon('Semester')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700"
                    onClick={() => requestSort('LeoClubStatus')}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon('LeoClubStatus')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 hidden xl:table-cell"
                    onClick={() => requestSort('Role')}
                  >
                    <div className="flex items-center">
                      Role
                      {getSortIcon('Role')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 hidden sm:table-cell"
                    onClick={() => requestSort('RegDate')}
                  >
                    <div className="flex items-center">
                      Registered
                      {getSortIcon('RegDate')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {currentItems.map((member) => {
                  // Format registration date
                  const registeredDate = (() => {
  if (!member.RegDate) return 'N/A';
  const d = new Date(member.RegDate);
  return isNaN(d) ? 'N/A' : d.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' });
})();


                  return (
                    <tr key={member.SLIITLeoID} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-2 py-2 md:px-4 md:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-300 font-mono">
                        {member.SLIITLeoID || 'N/A'}
                      </td>
                      <td className="px-2 py-2 md:px-4 md:py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-white">
                        <div className="flex items-center">
                          <img 
                            className="h-8 w-8 rounded-full mr-2 bg-gray-700" 
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(member.FullName || '')}&background=4f46e5&color=fff`}
                            alt={member.FullName} 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://ui-avatars.com/api/?name=U&background=4f46e5&color=fff';
                            }}
                          />
                          <span className="truncate max-w-[150px] md:max-w-xs">
                            {member.FullName || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell font-mono">
                        {member.SLIITID || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 hidden lg:table-cell">
                        {member.Faculty || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                        {member.Year || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 hidden lg:table-cell">
                        {member.Semester || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          member.LeoClubStatus === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : member.LeoClubStatus === 'Inactive'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {member.LeoClubStatus || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 hidden xl:table-cell">
                        {member.Role || 'Club Member'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400 hidden sm:table-cell">
                        {registeredDate}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {filteredMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No members found matching your criteria
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-800 border-t border-gray-700">
                <div className="text-sm text-gray-400 mb-2 sm:mb-0">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, filteredMembers.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredMembers.length}</span> results
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-md bg-gray-700 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    if (pageNum > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === pageNum 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-md bg-gray-700 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
}
